import { useT } from "../TranslatedText";
import {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";
import styles from "./styles/CleverTable.module.scss";
import { CSSTransition } from "react-transition-group";
import classNames from "classnames";
import InfiniteScroller from "../../header/InfiniteScroller";
import TrackVisibility from "react-on-screen";
import FontAwesome from "../FontAwesome";
import Scrollbar from "react-scrollbars-custom";
import { reactScrollBarsCustomWidth } from "../../../utils/SchoolBlocksUtilities";
import Button from "../Button";

interface ICleverTableColumn {
  title: ReactNode;
  dataKey: string;
  initialWidth?: number;
}

interface ICleverTableRow {
  onClick?: () => void;
  key: any;
  style?: React.CSSProperties;
  height?: number;
  analyticsEvent?: string;
  analyticsProperties?: any;
  [key: string]: any;
}

interface ICleverTableProps {
  data?: ICleverTableRow[];
  columns?: ICleverTableColumn[];
  initialWidthIsPercent?: boolean;
  className?: string;
  tableRowClassName?: string;
  noDataMessage?: string;
  handleFetchMore?: () => void;
  shouldFetchMore?: boolean;
  maxHeight?: number | string;
  selectedItemKey?: any;
  transitionIn?: boolean;
  transitionClassNames?: any;
  useInfiniteScroll?: boolean;
  loading?: boolean;
  height?: any;
}

export default function CleverTable(props: ICleverTableProps) {
  const t = useT();
  const {
    transitionIn = true,
    data = [],
    columns = [],
    className = "",
    maxHeight = 400,
    initialWidthIsPercent = false,
    useInfiniteScroll = false,
  } = props;
  const [columnWidths, setColumnWidths] = useState<(number | undefined)[]>(
    columns.map((c) => c.initialWidth),
  );

  const [dragging, setDragging] = useState(false);
  const [currentDragColumn, setCurrentDragColumn] = useState<number | null>(
    null,
  );
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [initialWidths, setInitialWidths] = useState<
    (number | undefined)[] | null
  >(null);
  const [currentDragPosition, setCurrentDragPosition] = useState<number | null>(
    null,
  );
  const transitionRef = useRef(null);

  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dragging) {
      setColumnWidths(columns.map((c) => c.initialWidth));
    }
  }, [columns]);

  const handleDragColumn = useCallback(
    (dragged: number) => {
      setColumnWidths((current) => {
        return current.map((c, i) => {
          if (i === currentDragColumn) {
            return initialWidths![i]! - dragged;
          } else if (i === currentDragColumn! + 1) {
            return initialWidths![i]! + dragged;
          }
          return initialWidths![i];
        });
      });
    },
    [columnWidths, initialWidths, currentDragColumn],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent, columnIndex: number) => {
      if (e.button === 0) {
        setDragging(true);
        setCurrentDragColumn(columnIndex);
        setDragStart(e.screenX);
        setInitialWidths(columnWidths);
      }
    },
    [columnWidths],
  );

  const handleMouseUp = useCallback(() => {
    if (dragging) {
      setDragging(false);
      setCurrentDragColumn(null);
      setInitialWidths(null);
      setCurrentDragPosition(null);
      setDragStart(null);
    }
  }, [dragging]);

  const handleMouseMove = useCallback(
    (e: { screenX: number }) => {
      if (dragging) {
        setCurrentDragPosition(e.screenX);
      }
    },
    [dragging],
  );

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [dragging]);

  useEffect(() => {
    let dragged: number; // positive if dragged left, negative if dragged right
    if (initialWidthIsPercent) {
      dragged =
        ((dragStart! - currentDragPosition!) / tableRef.current!.clientWidth) *
        100;
    } else {
      dragged = dragStart! - currentDragPosition!;
    }

    if (typeof currentDragColumn === "number" && currentDragPosition) {
      handleDragColumn(dragged);
    }
  }, [currentDragPosition]);

  const { columnDividers, cumulativeWidth } = useMemo(() => {
    let cumulativeWidth = 0;
    const cols = columns.reduce((acc: ReactNode[], curr, idx) => {
      // eslint-disable-next-line react-hooks/immutability
      cumulativeWidth += columnWidths[idx] ?? 0;

      if (idx < columns.length - 1) {
        // don't add a divider for the last column
        acc.push(
          <span
            key={curr.dataKey}
            onMouseDown={(e) => handleMouseDown(e, idx)}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            style={{
              left: cumulativeWidth + (initialWidthIsPercent ? "%" : "px"),
              height: 50 * (data.length + 1),
            }}
          />,
        );
      }

      return acc;
    }, []);
    return { columnDividers: cols, cumulativeWidth };
  }, [
    columns.length,
    data.length,
    initialWidthIsPercent,
    props.height,
    columnWidths,
  ]);

  const containerClassName = classNames({
    [styles.table]: true,
    [className]: true,
  });

  const tableRowClassName = classNames({
    [styles.tableRow]: true,
    [props.tableRowClassName ?? ""]: props.tableRowClassName,
  });

  const listOfCellHeightsByIndex = data.map((d) => {
    return d.height ?? 0;
  });

  // Helper functions to avoid row rendering duplication
  const renderRowCells = (d: ICleverTableRow, rowIndex: number) => {
    return columns.map((c, colIndex) => (
      <div
        className={styles.tableCell}
        key={c.dataKey}
        style={{
          width: columnWidths[colIndex] + (initialWidthIsPercent ? "%" : "px"),
          height: listOfCellHeightsByIndex[rowIndex],
        }}
      >
        {d[c.dataKey]}
      </div>
    ));
  };

  const getRowStyle = (d: ICleverTableRow, rowIndex: number) => ({
    transform: `translateY(${listOfCellHeightsByIndex.slice(0, rowIndex).reduce((partialSum, height) => partialSum + height, 0)}px)`,
    height: listOfCellHeightsByIndex[rowIndex],
    width: initialWidthIsPercent ? "100%" : `${cumulativeWidth}px`,
    cursor: d.onClick ? "pointer" : "inherit",
    ...(d.style || {}),
  });

  const renderRow = (d: ICleverTableRow, rowIndex: number, ref: any) => {
    if (d.onClick) {
      return (
        <Button
          as="div"
          ref={ref}
          className={tableRowClassName}
          style={getRowStyle(d, rowIndex)}
          analyticsEvent={d.analyticsEvent}
          analyticsProperties={d.analyticsProperties}
          onKeyDown={(e) => {
            if (e.which === 13) {
              e.currentTarget.click();
            }
          }}
          onClick={() => d.onClick?.()}
        >
          {renderRowCells(d, rowIndex)}
        </Button>
      );
    }

    return (
      <div
        ref={ref}
        className={tableRowClassName}
        tabIndex={-1}
        role="none"
        style={getRowStyle(d, rowIndex)}
      >
        {renderRowCells(d, rowIndex)}
      </div>
    );
  };

  return (
    <div
      className={containerClassName}
      ref={tableRef}
      style={{
        height:
          listOfCellHeightsByIndex.reduce(
            (partialSum, height) => partialSum + height,
            0,
          ) +
          (1 +
            (data.length === 0 || props.shouldFetchMore || props.loading
              ? 1
              : 0)) *
            50,
        width: initialWidthIsPercent ? "100%" : `${cumulativeWidth}px`,
        maxHeight: maxHeight,
      }}
    >
      <div className={styles.columnDividerContainer}>{columnDividers}</div>
      <div
        className={styles.tableRow}
        style={{
          width: initialWidthIsPercent ? "100%" : `${cumulativeWidth}px`,
        }}
      >
        {columns.map((c, i) => {
          let width;
          if (initialWidthIsPercent) {
            width = `calc(${columnWidths[i]}% - ${10 / columns.length}px)`;
          } else {
            width = columnWidths[i] + "px";
          }
          return (
            <div
              className={styles.tableCell}
              style={{
                width,
                height: "50px",
              }}
              key={c.dataKey}
            >
              {c.title}
            </div>
          );
        })}
      </div>
      {data.length === 0 && props.noDataMessage && (
        <div
          style={{
            textAlign: "center",
            paddingTop: "1rem",
            width: "100%",
          }}
        >
          {props.noDataMessage}
        </div>
      )}
      <Scrollbar
        scrollerProps={{
          tabIndex: 0,
          role: "region",
          "aria-label": t("Table contents"),
        }}
        scrollbarWidth={reactScrollBarsCustomWidth}
        disableTrackYWidthCompensation={true}
        style={{ height: `calc(100% - ${50}px)` }}
      >
        {data.map((d, i) => {
          const ref = createRef();
          return (
            <CSSTransition
              nodeRef={ref}
              appear
              key={d.key}
              data-selected={d.key === props.selectedItemKey}
              in={transitionIn}
              timeout={250}
              classNames={props.transitionClassNames}
            >
              {renderRow(d, i, ref)}
            </CSSTransition>
          );
        })}
        <CSSTransition
          nodeRef={transitionRef}
          timeout={250}
          key={"loadmore"}
          appear
          in={true}
          classNames={props.transitionClassNames}
        >
          {(props.shouldFetchMore && props.handleFetchMore) || props.loading ? (
            <div
              ref={transitionRef}
              style={{
                transform: `translateY(${listOfCellHeightsByIndex.reduce((partialSum, height) => partialSum + height, 0)}px)`,
                textAlign: "center",
                height: 50,
                lineHeight: 50 + "px",
              }}
            >
              {props.loading && (
                <>
                  <FontAwesome spin prefix={"fas"} name={"fa-spinner"} />
                  &nbsp;
                </>
              )}
              {props.shouldFetchMore && props.handleFetchMore && (
                <>
                  {!useInfiniteScroll && (
                    <button
                      disabled={props.loading}
                      onClick={props.handleFetchMore}
                    >
                      Load More
                    </button>
                  )}
                  {useInfiniteScroll && (
                    <TrackVisibility throttleInterval={2000} offset={275}>
                      <InfiniteScroller fetchMore={props.handleFetchMore} />
                    </TrackVisibility>
                  )}
                </>
              )}
            </div>
          ) : (
            <div />
          )}
        </CSSTransition>
      </Scrollbar>
    </div>
  );
}
